import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import { Button, Icon, Text } from '@chakra-ui/react'
import { Base64 } from 'js-base64'
import _ from 'lodash'
import React, { useContext, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { RiDeleteBinLine } from 'react-icons/ri'
import { useHistory } from 'react-router-dom'
import useFetch, { CachePolicies } from 'use-http'
import { useGetQueryParams } from '../../../hooks/useGetQueryParams'
import useToast from '../../../hooks/useToast'
import type {
  DeleteFilesBulkErrorResponse,
  DeleteFilesBulkSuccessResponse,
  DeleteFolderErrorResponse,
  ViewableParentSuccessResponse,
} from '../../../interfaces'
import { MLContext } from '../../../providers'
import { MediaLibraryActions } from '../../../providers/MLProvider'

interface Props {
  isOpen: boolean
  onClose(): void
}

const DeleteMediaModal = ({ isOpen, onClose }: Props): React.ReactElement => {
  const { t } = useTranslation()
  const toast = useToast()
  const [isSubmitting, setSubmitting] = useState<boolean>(false)

  const [
    {
      selectedFiles,
      selectedFolders,
      selectedBreadcrumbFolder,
      mediaCollectionRoute,
      currentMediaCollection,
      viewableParent,
      refetch,
      refetchDrawerFolders,
    },
    dispatch,
  ] = useContext(MLContext)

  const { response, request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const history = useHistory()

  const queryFromUrl = useGetQueryParams()

  const deleteMedia = async (): Promise<void | boolean> => {
    let isBreadcrumbFolderDeleted = false
    if (selectedFiles.length > 0) {
      const fileIds = selectedFiles.map(file => file.fileId)
      const res = (await request.post('/api/v2/files/batch/deleteByFileIds', {
        fileIds,
      })) as DeleteFilesBulkSuccessResponse | DeleteFilesBulkErrorResponse

      if (request.error) {
        if ([400, 404].includes(response.status)) {
          toast({
            description: res.message,
            status: 'error',
            isClosable: true,
            duration: 5000,
            position: 'top-right',
            variant: 'outline',
          })
        } else {
          toast({
            description: t(
              'error_in_deleting_files',
              'Error in deleting file(s), please try again after sometime',
            ),
            status: 'error',
            isClosable: true,
            duration: 5000,
            position: 'top-right',
            variant: 'outline',
          })
        }
      } else if (response.status === 207) {
        const count = (res as DeleteFilesBulkSuccessResponse)
          .successfullyDeletedFileIds.length
        toast({
          description: (
            <Trans t={t} i18nKey="file_deleted_partially" count={count}>
              {{ count }} file(s) deleted successfully. {res.message}
            </Trans>
          ),
          status: 'info',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
      } else {
        const count = (res as DeleteFilesBulkSuccessResponse)
          .successfullyDeletedFileIds.length
        toast({
          description: (
            <Trans t={t} i18nKey="file_deleted_successfully" count={count}>
              {{ count }} file(s) deleted successfully
            </Trans>
          ),
          status: 'success',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
      }
    }

    if (selectedFolders.length > 0) {
      const deletedFoldersStatus: number[] = []
      const folderPaths = selectedFolders.map(folder => folder.folderPath)

      for await (const folderPath of folderPaths) {
        const res = (await request.delete('/api/v2/folder/', {
          folderPath,
        })) as null | DeleteFolderErrorResponse

        if (request.error) {
          if ([400, 404].includes(response.status)) {
            toast({
              description: res?.message,
              status: 'error',
              isClosable: true,
              duration: 5000,
              position: 'top-right',
              variant: 'outline',
            })
          } else {
            toast({
              description: (
                <Trans t={t} i18nKey="error_in_deleting_folder">
                  Error in deleting folder {{ folderPath }}, please try again
                  after sometime
                </Trans>
              ),
              status: 'error',
              isClosable: true,
              duration: 5000,
              position: 'top-right',
              variant: 'outline',
            })
          }
        } else {
          if (selectedBreadcrumbFolder?.folderPath === folderPath) {
            isBreadcrumbFolderDeleted = true
          }
          deletedFoldersStatus.push(response.status)
        }
      }
      if (deletedFoldersStatus.length > 0) {
        const count = deletedFoldersStatus.length
        toast({
          description: (
            <Trans t={t} i18nKey="folder_deleted_successfully" count={count}>
              {{ count }} folder(s) deleted successfully
            </Trans>
          ),
          status: 'success',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })

        if (isBreadcrumbFolderDeleted) {
          let folderPath = selectedBreadcrumbFolder?.folderPath as string
          let viewableFolderPath = folderPath
          let unViewableBasePath = ''
          let newFolderPath = ''

          if (
            viewableParent &&
            !_.isUndefined(
              (viewableParent as ViewableParentSuccessResponse).asset,
            )
          ) {
            const index = (
              viewableParent as ViewableParentSuccessResponse
            ).asset?.assetPath.lastIndexOf('/')
            if (index) {
              viewableFolderPath = folderPath.slice(index, folderPath.length)
              unViewableBasePath = folderPath.slice(0, index)
            }
          }

          const folderPathArray = viewableFolderPath.split('/')
          if (
            folderPathArray[folderPathArray.length - 1] ===
            selectedBreadcrumbFolder?.name
          ) {
            folderPathArray.pop()
          }
          viewableFolderPath = folderPathArray.join('/')

          if (viewableFolderPath && viewableFolderPath !== '/') {
            newFolderPath = `${unViewableBasePath}${viewableFolderPath}`
          }

          mediaCollectionRoute
            ? history.replace(
                `/media-collections/${currentMediaCollection}/${Base64.encode(
                  newFolderPath,
                  true,
                )}${queryFromUrl as string}`,
              )
            : history.replace(
                `/media-library/${Base64.encode(newFolderPath, true)}${
                  queryFromUrl as string
                }`,
              )
        }
      }
    }

    return isBreadcrumbFolderDeleted
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Icon
            as={RiDeleteBinLine}
            color="appBattleshipGrey"
            backgroundColor="appPaleGrey"
            boxSize={6}
            padding="0.1em"
            borderRadius="full"
            marginRight="0.5em"
          />
          {t('delete_media', 'Delete Media')}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text color="appBattleshipGrey">
            {t(
              'delete_media_description',
              `This will permanently delete all the selected files, 
              folders and content in the selected folder. Once submitted, 
              this action can not be reversed.`,
            )}
          </Text>
        </ModalBody>
        <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
          <Button onClick={onClose} variant="outline">
            {t('cancel', 'Cancel')}
          </Button>
          <Button
            colorScheme="appRed"
            isDisabled={isSubmitting}
            onClick={async () => {
              setSubmitting(true)
              const isBreadcrumbFolderDeleted = await deleteMedia()
              setSubmitting(false)
              onClose()
              if (!isBreadcrumbFolderDeleted) {
                await new Promise(resolve => setTimeout(resolve, 1500))
                await refetch()
                await refetchDrawerFolders()
                dispatch({
                  type: MediaLibraryActions.DESELECT_ALL_FILES_AND_FOLDERS,
                })
              }
            }}
          >
            {t('submit', 'Submit')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default DeleteMediaModal
